// src/ryan/admin/components/tabs/RyanAdminHub.tsx
//
// Casca de navegação unificada — substitui as duas páginas antigas
// (RyanAdminSales.tsx e RyanAdminProducts.tsx, cada uma com sua própria barra
// de abas) por UMA barra só, na ordem: Acesso, Produtos, Marketing, Analytics,
// Cupons, Embaixadores, Vendas, Rentabilidade, Configurações (ícone). Não
// reimplementa nada — só delega pro conteúdo já existente de cada aba
// (AccessTab/OverviewTab/ProfitabilityTab de RyanAdminSales.tsx,
// RyanAdminProducts inteiro controlado externamente pras suas 2 sub-abas,
// RyanRepresentatives, RyanAdminSettings). "Solicitações" e "Histórico" como
// abas próprias saíram da barra (ver comentários abaixo de onde foram parar),
// sem nenhuma rota de backend nem componente removido. "Campanhas" também
// saiu da barra — virou um modo dentro de Marketing (RyanAdminProducts.tsx,
// mktMode). "Leads Pendentes" seguiu o mesmo caminho — virou um modo dentro
// do select de Analytics (RyanAdminAnalytics.tsx), não é mais posição própria
// aqui; RyanAdminLeads.tsx continua existindo, só passou a ser montado de lá.
import React, { useState, useEffect, useCallback } from 'react';
import { Settings } from 'lucide-react';
import { AccessTab, OverviewTab, ProfitabilityTab } from './RyanAdminSales';
import RyanAdminProducts from './RyanAdminProducts';
import RyanRepresentatives from './RyanRepresentatives';
import RyanAdminAnalytics from './RyanAdminAnalytics';
import RyanAdminCoupons from './RyanAdminCoupons';
import RyanAdminSettings from '../../../components/admin/RyanAdminSettings';
import './RyanAdminSales.css';

type ProductsInnerTab = 'produtos' | 'marketing';
type HubTab = 'access' | ProductsInnerTab | 'analytics' | 'coupons' | 'ambassadors' | 'overview' | 'profitability' | 'settings';

const HUB_TAB_ORDER: HubTab[] = [
  'access', 'produtos', 'marketing', 'analytics', 'coupons', 'ambassadors', 'overview', 'profitability', 'settings',
];

const PRODUCTS_SUBTABS: HubTab[] = ['produtos', 'marketing'];

// título/subtítulo pras abas que a própria RyanAdminHub decide — as 3 de
// produtos ficam de fora aqui de propósito: RyanAdminProducts já sabe seus
// próprios títulos (evita manter o mesmo texto duplicado em dois lugares).
const TAB_META: Partial<Record<HubTab, { label: string; subtitle: string }>> = {
  access:        { label: 'Acesso',        subtitle: 'Conceda e revogue acessos a produtos.' },
  analytics:     { label: 'Analytics',      subtitle: 'Funil de visitantes até a conversão em venda, por produto.' },
  coupons:       { label: 'Cupons',         subtitle: 'Crie cupons de desconto e acompanhe uso e receita gerada.' },
  ambassadors:   { label: 'Embaixadores',   subtitle: 'Cadastre embaixadores, acompanhe vendas indicadas e comissões.' },
  overview:      { label: 'Vendas',         subtitle: 'Visão geral de receita, desempenho de vendas e histórico completo de transações.' },
  profitability: { label: 'Rentabilidade',  subtitle: 'Analise despesas, receitas e lucros.' },
  settings:      { label: 'Configurações',  subtitle: 'Configurações da plataforma e ferramentas de manutenção.' },
};

const TAB_LABELS: Record<HubTab, string> = {
  access: 'Acesso',
  produtos: 'Produtos',
  marketing: 'Marketing',
  analytics: 'Analytics',
  coupons: 'Cupons',
  ambassadors: 'Embaixadores',
  overview: 'Vendas',
  profitability: 'Rentabilidade',
  settings: 'Configurações',
};

interface RyanAdminHubProps {
  onHeaderChange: (title: string, subtitle: string) => void;
}

const RyanAdminHub: React.FC<RyanAdminHubProps> = ({ onHeaderChange }) => {
  const [tab, setTab] = useState<HubTab>('access');

  useEffect(() => {
    // As 3 abas de produtos setam o próprio header de dentro de
    // RyanAdminProducts (mesmo onHeaderChange, repassado direto) — evita
    // chamar onHeaderChange duas vezes com textos que precisariam ficar
    // sincronizados manualmente em dois arquivos.
    if (PRODUCTS_SUBTABS.includes(tab)) return;
    const meta = TAB_META[tab];
    if (meta) onHeaderChange(meta.label, meta.subtitle);
  }, [tab, onHeaderChange]);

  // Handler estável (useCallback) pra passar como onTabChange do
  // RyanAdminProducts sem recriar a função a cada render.
  const handleProductsTabChange = useCallback((t: ProductsInnerTab) => setTab(t), []);

  return (
    <div className="ras-root">
      <div className="ras-tabs">
        {HUB_TAB_ORDER.map(t => (
          <button
            key={t}
            className={`ras-tab${tab === t ? ' ras-tab--active' : ''}${t === 'settings' ? ' ras-tab--icon-only' : ''}`}
            onClick={() => setTab(t)}
            title={t === 'settings' ? 'Configurações' : undefined}
            aria-label={t === 'settings' ? 'Configurações' : undefined}
          >
            {t === 'settings' ? <Settings size={17} /> : TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === 'access' && <AccessTab />}

      {/* Produtos/Marketing — RyanAdminProducts inteiro, controlado
          externamente (activeTab/onTabChange) pra tratar essas 2 posições
          como abas de primeiro nível desta barra, sem duplicar nem tocar em
          nada do conteúdo interno dele. */}
      {PRODUCTS_SUBTABS.includes(tab) && (
        <RyanAdminProducts
          onHeaderChange={onHeaderChange}
          activeTab={tab as ProductsInnerTab}
          onTabChange={handleProductsTabChange}
        />
      )}

      {tab === 'analytics' && <RyanAdminAnalytics />}
      {tab === 'coupons' && <RyanAdminCoupons />}
      {tab === 'ambassadors' && <RyanRepresentatives />}
      {tab === 'overview' && <OverviewTab />}
      {tab === 'profitability' && <ProfitabilityTab />}
      {/* "Solicitações" não é mais uma posição da barra — o fluxo de
          aprovação manual de comprovante deixou de ser o caminho padrão
          (pagamento é automático via Mercado Pago). O painel "Aprovações
          manuais (legado)" que ficava dentro de Configurações foi removido
          da interface por completo (o admin não acessa mais isso pela
          tela) — RyanAccessRequests.tsx e as rotas de backend (PATCH
          .../approve etc.) não foram tocadas, continuam funcionando via
          chamada direta à API se um dia forem genuinamente necessárias. */}
      {tab === 'settings' && <RyanAdminSettings />}
    </div>
  );
};

export default RyanAdminHub;
